import { useEffect, useMemo, useRef } from 'react';

import { getRovoParams } from '@atlaskit/rovo-triggers/params';
import { type RovoChatParams } from '@atlaskit/rovo-triggers/params-types';

import { useTownsquareApp } from '@townsquare/app-context';
import { useRovoWorkspace } from '@townsquare/collab-context';
import { location } from '@townsquare/facade/window';
import { useOrgContextCloudIds } from '@townsquare/org-context';
import { fg } from '@townsquare/stat-sig/gate';

import { useRovoEntitlement } from './entitlement-resource';

/**
 * Separate logic for Nav3
 */
export function useRovoNavigationItemsNav3(): {
	isRovoEnabled: boolean;
	isRovoLLMEnabled: boolean;
} {
	const { data, loading, error } = useRovoEntitlement();

	if (loading || error || !data) {
		return { isRovoEnabled: false, isRovoLLMEnabled: false };
	}

	return {
		isRovoLLMEnabled: data.isRovoLLMEnabled,
		isRovoEnabled: data.isRovoEnabled,
	};
}

export const getCloudIdForRovo = <T>(rovoParams: RovoChatParams, workspaceCloudId: T): string | T =>
	rovoParams?.cloudId ?? workspaceCloudId;

export function useRovoParams(): { cloudId: string; rovoParams: RovoChatParams } {
	const rovoParams = useMemo(() => {
		const params = getRovoParams();
		return params;
	}, []);

	const workspaceCloudId = useRovoWorkspace();
	const cloudIds = useOrgContextCloudIds();

	// Retain the last resolved cloudId. Workspace/org context resolves asynchronously, so on direct
	// navigation the resolved value can briefly be empty before it settles. Without this, cloudId
	// flip-flops between '' and the real id, recreating every downstream memo keyed on it (e.g. the
	// assistance service) and re-firing their effects — which manifested as endless conversations /
	// pinned-summary requests on the "View all conversations" page. Guarded by a killswitch so the
	// stabilisation can be disabled in production if it causes unexpected behaviour.
	const lastResolvedCloudIdRef = useRef('');
	const resolvedCloudId = getCloudIdForRovo(rovoParams, workspaceCloudId ?? cloudIds?.at(0) ?? '');
	if (resolvedCloudId) {
		lastResolvedCloudIdRef.current = resolvedCloudId;
	}
	const cloudId = fg('rovo_chat_infinite_load_townsquare_fix_killswitch')
		? resolvedCloudId || lastResolvedCloudIdRef.current
		: resolvedCloudId;

	return {
		cloudId,
		rovoParams,
	};
}

export type RovoProduct = 'home' | 'projects' | 'goals' | 'teams' | 'rovo';

/**
 * Derives the Rovo chat `product` header from the current Townsquare app, gated behind
 * `townsquare_enable_rovo_ubp_header`. When the gate is off the header stays `'rovo'`.
 */
export function useRovoProductHeader(): RovoProduct {
	const app = useTownsquareApp();
	if (!fg('townsquare_enable_rovo_ubp_header')) {
		return 'rovo';
	}
	switch (app) {
		case 'home':
			return 'home';
		case 'projects':
			return 'projects';
		case 'goals':
			return 'goals';
		case 'teams':
		case 'teams-isolated':
			return 'teams';
		default:
			return 'rovo';
	}
}

export function useAssistanceServiceParams({
	experienceId,
	touchpointSource,
}: {
	experienceId: string;
	touchpointSource: string;
}): {
	product: RovoProduct;
	experienceId: string;
	touchpointSource: string;
	siteId: string;
} {
	const { cloudId } = useRovoParams();
	const product = useRovoProductHeader();
	return useMemo(
		() => ({
			product,
			experienceId,
			touchpointSource,
			siteId: cloudId,
		}),
		[cloudId, experienceId, touchpointSource, product],
	);
}

const ROVO_MARKETING_URL = 'https://atlassian.com/software/rovo';
/**
 * Redirects to the Rovo marketing page if the user does not have Rovo/LLM entitlement.
 * Used in ChatView, AgentsView, and SearchView.
 *
 * @param isSearchView - Whether the view is the search view. Defaults to false. If true, the user must have Rovo entitlement. If false, it checks for LLM entitlement specifically.
 */
export function useRovoEntitlementRedirect({
	isSearchView = false,
}: {
	isSearchView?: boolean;
}): boolean {
	const rovoEntitlement = useRovoEntitlement();
	const entitlementToCheck = isSearchView
		? rovoEntitlement.data?.isRovoEnabled
		: rovoEntitlement.data?.isRovoLLMEnabled;

	useEffect(() => {
		if (rovoEntitlement.data && !entitlementToCheck) {
			location().replace(ROVO_MARKETING_URL);
		}
	}, [rovoEntitlement.data, entitlementToCheck]);

	const shouldRenderNull = rovoEntitlement.loading || !rovoEntitlement.data || !entitlementToCheck;
	return shouldRenderNull;
}
